declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
    }
}

const SCRIPT_ID = 'google-analytics';
const CONSENT_WAIT_MS = 500;
const COOKIE_PREFIX = '_ga';
const EXPIRED = 'Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * gtag.js only treats dataLayer entries that are real arguments objects as commands.
 * Pushing an array would silently drop every command queued before the script loads.
 */
function ensureGtag(): (...args: unknown[]) => void {
    window.dataLayer = window.dataLayer ?? [];
    if (!window.gtag) {
        window.gtag = function gtag() {
            /* eslint-disable-next-line prefer-rest-params */
            window.dataLayer?.push(arguments);
        };
    }
    return window.gtag;
}

export function loadGoogleAnalytics(measurementId: string): void {
    const gtag = ensureGtag();
    if (!document.getElementById(SCRIPT_ID)) {
        gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            wait_for_update: CONSENT_WAIT_MS
        });
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
        document.head.append(script);
        gtag('js', new Date());
        gtag('config', measurementId, {
            send_page_view: false,
            anonymize_ip: true,
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
            debug_mode: import.meta.env.DEV
        });
    }
    gtag('consent', 'update', { analytics_storage: 'granted' });
}

function hostCandidates(): string[] {
    const parts = window.location.hostname.split('.');
    const root = parts.length > 1 ? parts.slice(-2).join('.') : window.location.hostname;
    return [window.location.hostname, `.${window.location.hostname}`, `.${root}`];
}

function deleteAnalyticsCookies(): void {
    const names = document.cookie
        .split(';')
        .map((entry) => entry.trim().split('=')[0])
        .filter((name) => name === COOKIE_PREFIX || name.startsWith(`${COOKIE_PREFIX}_`));
    names.forEach((name) => {
        document.cookie = `${name}=; expires=${EXPIRED}; path=/`;
        hostCandidates().forEach((domain) => {
            document.cookie = `${name}=; expires=${EXPIRED}; path=/; domain=${domain}`;
        });
    });
}

export function revokeGoogleAnalytics(): void {
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
    deleteAnalyticsCookies();
}

export function trackPageView(path: string, title: string, language: string): void {
    window.gtag?.('event', 'page_view', {
        page_path: path,
        page_title: title,
        page_location: window.location.href,
        language
    });
}

export function trackEvent(name: string, params: Record<string, string | number | boolean>): void {
    window.gtag?.('event', name, params);
}
